import type { BarModel } from './bars.types';
import type { ClientModel } from './clients.types';
import type { MediaModel } from './medias.types';
import type { NotificationsType } from './notifications.types';
import type { RoleModel } from './roles.types';
import type { TechnicalSupportProviderModel } from './technical-support-providers.types';
import type { VenueModel } from './venues.types';
import type { VisitModel } from './visits.types';

export enum UserEmploymentStatusType {
  ACTIVE = 'Active',
  WENT_TO_NON_CLIENT = 'Went to non-client',
  LEFT_INDUSTRY = 'Left industry'
}

export interface UserModel {
  id: string;
  cognitoUser: string;
  firstName: string;
  lastName: string;
  email?: string;
  lastLogin?: Date;
  roles: RoleModel[];
  clients: ClientModel[];
  mobilePhone: string;
  contactType: boolean;
  status: UserStatusType;
  jobRole: JobRoleType | null;
  employmentStatus: UserEmploymentStatusType | null;
  technicalSupportProviders: TechnicalSupportProviderModel[];
  venues: VenueModel[];
  bars: BarModel[];
  visits: VisitModel[];
  avatar: MediaModel | null;
  permissions: string[];
  userSubscriptions: UserModel[];
  userNotifications: UserNotifications[];
}

export interface UserNotifications {
  notificationsId: string;
  usersId: string;
  notification: NotificationsType;
}

export enum UserStatusType {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  INVITED = 'INVITED',
  ARCHIVED = 'ARCHIVED'
}

export enum JobRoleType {
  OWNER = 'Owner / MD',
  DIRECTOR = 'Director / Dept Head',
  BUSINESS_DEVELOPMENT = 'Business Development/ Procurement',
  OPS_MANAGER = 'Ops / Regional Manager',
  GENERAL_MANAGER = 'General Manager',
  BAR_MANAGER = 'F&B / Bar Manager',
  ASSISTANT_MANAGER = 'Assistant Manager',
  STOCK_MANAGER = 'Stock Manager / Team Member',
  AVANI_SOLUTIONS = 'Avani Solutions',
  CONTACT_ONLY_CLEANER = 'Contact Only - Cleaner',
  CONTACT_ONLY_SECURITY = 'Contact Only - Security',
  CONTACT_ONLY_OTHER = 'Contact Only - Other'
}

export enum JobRoleTypeClient {
  OWNER = 'Owner / MD',
  DIRECTOR = 'Director / Dept Head',
  BUSINESS_DEVELOPMENT = 'Business Development/ Procurement',
  OPS_MANAGER = 'Ops / Regional Manager',
  GENERAL_MANAGER = 'General Manager',
  BAR_MANAGER = 'F&B / Bar Manager',
  ASSISTANT_MANAGER = 'Assistant Manager',
  STOCK_MANAGER = 'Stock Manager / Team Member'
}

export interface ResponseAWSUserParams {
  email: string;
}

export interface ListAutoCompleteUsersResponse {
  result: UserModel[];
  count: number;
}

export interface ListUsersResponse {
  result: UserListModel[];
  limit: number;
  offset: number;
  count: number;
}

export interface CreateUserParams extends UserParams {
  temporaryPassword?: string;
  contactType: boolean;
}

export interface UserParams {
  firstName?: string;
  lastName?: string;
  email?: string;
  mobilePhone?: string;
  employmentStatus?: UserEmploymentStatusType | null;
  jobRole?: JobRoleType | null;
  roleIds?: string[];
  venueIds?: string[];
  clientIds?: string[];
  clientId?: string;
  userSubscriptionIds?: string[];
  userNotificationIds?: string[];
}

export interface BulkUserParams {
  createUsers: UserParams[];
}

export interface UpdateUserParams extends UserParams {
  roles?: RoleModel[];
}

export interface UpdateUserParamsWithId extends UpdateUserParams {
  userId: string;
}

export interface DeleteUserParams {
  userId: string;
}

export interface ChangeUserPasswordParams {
  newPassword: string;
  confirmPassword: string;
}

export interface ChangeUserPasswordParamsWithId {
  userPassword: string;
  userId: string;
}

export interface ChangePasswordParams extends ChangeUserPasswordParams {
  oldPassword: string;
}

export interface SignInParams {
  email: string;
}

export interface SignInCodeParams {
  code: string;
}

export interface ForcePasswordParams {
  customPassword: string;
  confirmCustomPassword: string;
}

export interface ForgotPasswordParams {
  email: string;
}

export interface ForgotPasswordSubmitParams {
  code: string;
  password: string;
  confirmPassword: string;
}

export type UserListModel = Pick<UserModel, 'id' | 'status' | 'email' | 'mobilePhone' | 'lastLogin'> & {
  fullName: string | null;
  clientNames: string[];
  rolesNames: string[];
  venueNames: string[];
};

export type UserSortingFields = 'status' | 'email' | 'mobilePhone' | 'lastLogin' | 'fullName';

export interface UserFiltersProps {
  firstName?: string;
  lastName?: string;
  email?: string;
  mobilePhone?: string;
  status?: UserStatusType;
  jobRole?: JobRoleType[];
  roles?: string[];
  venues?: VenueModel[];
  clients?: ClientModel[];
}

export type SignificantRoleType = 'avaniAdmin' | 'clientAdmin' | 'venueUser' | 'clientRom';

// TODO: Replace with ENUMs
export const adminAvaniRoles = ['systemSuperAdmin', 'avaniAdmin', 'avaniRom', 'avaniTechnician'];

export interface BulkUserValidationRow {
  email: string;
  firstName: string;
  lastName: string;
  mobilePhone: string;
  jobRole: JobRoleType;
  client: { id: string; name: string | null } | null;
  venue: { id: string; name: string | null } | null;
  role: { id: string; name: string | null } | null;
  errors: string[];
}
